import { useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent } from '@mui/material';
import { Building2, School, Activity, ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdminDashboardStore } from '@/store/superAdmin/dashboardStore';
import { Loader } from '@/components/feedback/Loader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { tokens } from '@/theme/tokens';
import { PageHeader } from '@/components/navigation/PageHeader';
import { StatCard } from '@/components/cards/StatCard';

const ActivityFeed = ({ activities }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {activities.map((item) => (
      <Box key={item.id} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{
          width: 8, height: 8, mt: 1, borderRadius: '50%', flexShrink: 0,
          backgroundColor: item.type === 'success' ? tokens.colors.success.main : tokens.colors.info.main
        }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={600}>{item.title}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">{item.description}</Typography>
        </Box>
        <Typography variant="caption" color="text.muted" sx={{ whiteSpace: 'nowrap' }}>
          {item.time}
        </Typography>
      </Box>
    ))}
  </Box>
);

export function SuperAdminDashboardPage() {
  const navigate = useNavigate();
  const { stats, loading, error, fetchDashboardStats } = useSuperAdminDashboardStore();

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  if (loading) return <Loader message="Loading dashboard data..." />;
  if (error) return <ErrorState message={error} onRetry={fetchDashboardStats} />;
  if (!stats) return <Loader message="Initializing dashboard..." />;

  // Mapping recent orgs/schools to an activity feed format
  const recentActivities = [
    ...(stats.recentOrganizations || []).map(org => ({
      id: `org-${org.id}`,
      title: `New Organization Onboarded`,
      description: `${org.organization_name || org.name || 'Organization'} was successfully registered.`,
      time: new Date(org.created_at || org.createdAt || Date.now()).toLocaleDateString(),
      type: 'info',
    })),
    ...(stats.recentSchools || []).map(school => ({
      id: `school-${school.id}`,
      title: `New School Created`,
      description: `${school.school_name || school.name || 'School'} was added to the platform.`,
      time: new Date(school.created_at || school.createdAt || school.lastUpdated || Date.now()).toLocaleDateString(),
      type: 'success',
    })),
  ].sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 10);

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader 
        title="Super Admin Dashboard"
        subtitle="Platform overview and usage statistics"
      />

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Organizations"
            value={stats.totalOrganizations}
            icon={Building2}
            color={tokens.colors.primary.main}
            trend="+12% from last month"
            trendUp={true}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Schools"
            value={stats.totalSchools}
            icon={School}
            color={tokens.colors.info.main}
            trend="+5% from last month"
            trendUp={true}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Active Schools"
            value={stats.activeSchools}
            icon={Activity}
            color={tokens.colors.success.main}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Inactive Schools"
            value={stats.inactiveSchools}
            icon={ShieldAlert}
            color={tokens.colors.error.main}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card elevation={0} sx={{ height: '100%', borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}` }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Recent Platform Activities
              </Typography>
              {recentActivities.length > 0 ? (
                <ActivityFeed activities={recentActivities} />
              ) : (
                <Typography color="text.secondary">No recent activities found.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{ height: '100%', borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}` }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  onClick={() => navigate('/super-admin/organizations/add')}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2, p: 2,
                    borderRadius: tokens.radius.sm,
                    backgroundColor: tokens.colors.primary[50],
                    color: tokens.colors.primary.main,
                    cursor: 'pointer',
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    '&:hover': { backgroundColor: tokens.colors.primary[100] }
                  }}
                >
                  <Building2 size={20} /> Create Organization
                </Box>
                <Box
                  onClick={() => navigate('/super-admin/schools/add')}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2, p: 2,
                    borderRadius: tokens.radius.sm,
                    backgroundColor: tokens.colors.info[50],
                    color: tokens.colors.info.main,
                    cursor: 'pointer',
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    '&:hover': { backgroundColor: tokens.colors.info[100] }
                  }}
                >
                  <School size={20} /> Create School
                </Box>
                <Box
                  onClick={() => navigate('/super-admin/modules')}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2, p: 2,
                    borderRadius: tokens.radius.sm,
                    backgroundColor: tokens.colors.success[50],
                    color: tokens.colors.success.main,
                    cursor: 'pointer',
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    '&:hover': { backgroundColor: tokens.colors.success[100] }
                  }}
                >
                  <Activity size={20} /> Assign Modules
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
